import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { THEME_COLOR } from '../../../theme/formStyles';
import { LONG_TASK_DAYS, dayNumber, engineerName, fmtRange, siteColor, taskLength, todayYmd, itemKey } from '../projectSchedule.utils';

// Progress bar width and the right-hand label: done / day N of M / upcoming
const progressOf = (t, today) => {
    const len = taskLength(t);
    if (t.status === 'Completed') return { pct: 100, text: 'done' };
    if (today < t.start_date) return { pct: 0, text: 'upcoming' };
    const done = Math.min(len, dayNumber(t, today));
    return { pct: Math.round((done / len) * 100), text: `day ${done}/${len}` };
};

// Remembered while the page is open, so changing month or view keeps the line as it was
let rememberedOpen = false;

// Long tasks (3+ days) are listed once here instead of being drawn across every day of the calendar:
// one quiet line ("⏳ 4 long tasks running this month · names…"), opened for the details
export default function OngoingTasksStrip({ tasks, year, month, onOpenTask }) {
    const today = todayYmd();
    const [open, setOpen] = useState(rememberedOpen);
    const list = useMemo(() => {
        const first = dayjs().year(year).month(month).date(1);
        const ms = first.format('YYYY-MM-DD');
        const me = first.endOf('month').format('YYYY-MM-DD');
        return tasks
            .filter(t => taskLength(t) >= LONG_TASK_DAYS && t.start_date <= me && t.end_date >= ms)
            .sort((a, b) => taskLength(b) - taskLength(a));
    }, [tasks, year, month]);

    if (!list.length) return null;
    const toggle = () => { rememberedOpen = !open; setOpen(!open); };
    const names = list.slice(0, 3).map(t => t.sub_task_name).join(' · ') + (list.length > 3 ? ' …' : '');

    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, bgcolor: '#fafbfc', my: 1.5 }}>
            <ButtonBase onClick={toggle} aria-expanded={open}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'flex-start', px: 1.75, py: 1.25, borderRadius: 3, fontSize: '13px', color: '#46525e', textAlign: 'left' }}>
                <span>⏳</span>
                <Box component="b" sx={{ color: '#1f2933', fontWeight: 600, whiteSpace: 'nowrap' }}>{list.length} long task{list.length > 1 ? 's' : ''}</Box>
                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>running this month</Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0, color: '#6b7785', fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: { xs: 'none', sm: 'block' } }}>
                    {names}
                </Box>
                <Box component="span" sx={{ ml: 'auto', color: THEME_COLOR, fontWeight: 600, fontSize: '12.5px' }}>{open ? 'Hide' : 'Show'}</Box>
            </ButtonBase>
            {open && (
                <Box sx={{ borderTop: '1px solid #e5e9ee', p: '4px 6px 6px', maxHeight: 280, overflow: 'auto' }}>
                    {list.map(t => {
                        const color = siteColor(t.facility_id);
                        const p = progressOf(t, today);
                        return (
                            <ButtonBase key={itemKey(t)} onClick={() => onOpenTask(t)}
                                sx={{ display: 'grid', gridTemplateColumns: { xs: '10px minmax(0,1fr) auto', sm: '10px minmax(0,1fr) 110px 80px 70px' }, gap: 1.25, alignItems: 'center',
                                    width: '100%', p: 1, borderRadius: 2, textAlign: 'left', '&:hover': { bgcolor: '#fff' } }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontWeight: 500, fontSize: '13px' }}>{t.sub_task_name}</Typography>
                                    <Typography noWrap sx={{ fontSize: '11.5px', color: '#6b7785' }}>
                                        {t.facility_name} · <span style={t.spoc ? undefined : { color: '#dc2626', fontWeight: 700 }}>{engineerName(t)}</span>
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: '#6b7785', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>{fmtRange(t.start_date, t.end_date)}</Typography>
                                <Box sx={{ height: 5, borderRadius: '5px', bgcolor: '#e8eeec', overflow: 'hidden', display: { xs: 'none', sm: 'block' } }}>
                                    <Box sx={{ height: '100%', width: `${p.pct}%`, bgcolor: color }} />
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: '#6b7785', whiteSpace: 'nowrap', textAlign: 'right' }}>{p.text}</Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
